import React, { useState } from 'react';
import EditorialLayout from '@/components/editorial/EditorialLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Bot } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BriefBulletsManager } from '@/components/briefBullets/BriefBulletsManager';
import { BriefBulletsNattManager } from '@/components/briefBullets/BriefBulletsNattManager';
import { BriefBulletsDagManager } from '@/components/briefBullets/BriefBulletsDagManager';
import { BriefBulletsEuropaManager } from '@/components/briefBullets/BriefBulletsEuropaManager';
import { BriefBulletsNordAmerikaManager } from '@/components/briefBullets/BriefBulletsNordAmerikaManager';
import { BriefBulletsAsiaManager } from '@/components/briefBullets/BriefBulletsAsiaManager';
import { BriefBulletsAfrikaManager } from '@/components/briefBullets/BriefBulletsAfrikaManager';
import { BriefBulletsLatinAmerikaManager } from '@/components/briefBullets/BriefBulletsLatinAmerikaManager';
import { BriefBulletsMidtostenManager } from '@/components/briefBullets/BriefBulletsMidtostenManager';
import { BriefBulletsSkandiNaviaManager } from '@/components/briefBullets/BriefBulletsSkandiNaviaManager';
import { SummarySettings } from '@/components/engagement/SummarySettings';
import { InnspillSettings } from '@/components/engagement/InnspillSettings';
import { DebateSettings } from '@/components/engagement/DebateSettings';
import { ChatSettings } from '@/components/engagement/ChatSettings';
import { PollSettings } from '@/components/engagement/PollSettings';
import { QuizSettings } from '@/components/engagement/QuizSettings';
import { RagPromptViewer } from '@/components/rag/RagPromptViewer';

const AiDashboardPage: React.FC = () => {
  const [selectedArticle, setSelectedArticle] = useState<string>('brief-bullets');
  const [selectedEngagement, setSelectedEngagement] = useState<string>('summary');

  const renderArticleContent = () => {
    switch (selectedArticle) {
      case 'brief-bullets':
        return <BriefBulletsManager />;
      case 'brief-bullets-natt':
        return <BriefBulletsNattManager />;
      case 'brief-bullets-dag':
        return <BriefBulletsDagManager />;
      case 'brief-bullets-europa':
        return <BriefBulletsEuropaManager />;
      case 'brief-bullets-nord-amerika':
        return <BriefBulletsNordAmerikaManager />;
      case 'brief-bullets-asia':
        return <BriefBulletsAsiaManager />;
      case 'brief-bullets-afrika':
        return <BriefBulletsAfrikaManager />;
      case 'brief-bullets-latin-amerika':
        return <BriefBulletsLatinAmerikaManager />;
      case 'brief-bullets-midtosten':
        return <BriefBulletsMidtostenManager />;
      case 'brief-bullets-skandinavia':
        return <BriefBulletsSkandiNaviaManager />;
      case 'rag-prompt':
        return <RagPromptViewer />;
      default:
        return (
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground text-center">
                Velg en artikkeltype for å se innstillinger
              </p>
            </CardContent>
          </Card>
        );
    }
  };

  const renderEngagementContent = () => {
    switch (selectedEngagement) {
      case 'summary':
        return <SummarySettings />;
      case 'innspill':
        return <InnspillSettings />;
      case 'debate':
        return <DebateSettings />;
      case 'chat':
        return <ChatSettings />;
      case 'poll':
        return <PollSettings />;
      case 'quiz':
        return <QuizSettings />;
      default:
        return <SummarySettings />;
    }
  };

  return (
    <EditorialLayout>
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Bot className="w-8 h-8 text-primary" />
            AI Dashboard - Kai
          </h1>
          <p className="text-muted-foreground mt-1">
            Administrer automatisk artikkelproduksjon og engasjementsfunksjoner
          </p>
        </div>

        {/* Dropdown Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Article Production Category */}
          <Card>
            <CardHeader>
              <CardTitle>Artikkelproduksjon</CardTitle>
              <CardDescription>
                Velg type automatisk artikkel å administrere
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={selectedArticle} onValueChange={setSelectedArticle}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Velg artikkeltype" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="brief-bullets">Briefr Bullets: Hva skjer i dag?</SelectItem>
                  <SelectItem value="brief-bullets-natt">Briefr Bullets: Hva skjedde i natt?</SelectItem>
                  <SelectItem value="brief-bullets-dag">Briefr Bullets: Hva har skjedd i dag?</SelectItem>
                  <SelectItem value="brief-bullets-europa">🌍 Briefr Bullets: Hva skjer i Europa denne uken?</SelectItem>
                  <SelectItem value="brief-bullets-nord-amerika">🌍 Briefr Bullets: Hva skjer i Nord-Amerika denne uken?</SelectItem>
                  <SelectItem value="brief-bullets-asia">🌍 Briefr Bullets: Hva skjer i Asia denne uken?</SelectItem>
                  <SelectItem value="brief-bullets-afrika">🌍 Briefr Bullets: Hva skjer i Afrika denne uken?</SelectItem>
                  <SelectItem value="brief-bullets-latin-amerika">🌍 Briefr Bullets: Hva skjer i Latin-Amerika denne uken?</SelectItem>
                  <SelectItem value="brief-bullets-midtosten">🌍 Briefr Bullets: Hva skjer i Midtøsten denne uken?</SelectItem>
                  <SelectItem value="brief-bullets-skandinavia">🌍 Briefr Bullets: Hva skjer i Skandinavia denne uken?</SelectItem>
                  <SelectItem value="rag-prompt">🔧 RAG v2.1 - Teknisk Oversikt</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Engagement Tabs Category */}
          <Card>
            <CardHeader>
              <CardTitle>Engasjement-tabs</CardTitle>
              <CardDescription>
                Velg engasjementsfunksjon å konfigurere
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={selectedEngagement} onValueChange={setSelectedEngagement}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Velg funksjon" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Sammendrag av Kai</SelectItem>
                  <SelectItem value="innspill">Innspill</SelectItem>
                  <SelectItem value="debate">deBattle</SelectItem>
                  <SelectItem value="chat">Chat med Kai</SelectItem>
                  <SelectItem value="poll">Poll</SelectItem>
                  <SelectItem value="quiz">Quiz</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>

        {/* Content Display Area - Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Article Production Content */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Artikkelproduksjon</h2>
            {renderArticleContent()}
          </div>

          {/* Engagement Content */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Engasjementsfunksjoner</h2>
            {renderEngagementContent()}
          </div>
        </div>
      </div>
    </EditorialLayout>
  );
};

export default AiDashboardPage;
